// libraries
import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { Grid, Stack, Button, Typography, TextField } from '@mui/material'
import Webcam from 'react-webcam'

// queries
import { useUploadOrgImageMutation } from 'services/assets'
import GrossTareImagesLightbox from 'components/containers/inbound-tickets/components/GrossTareImagesLightbox'

// generic components
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import MaterialAndTruckImage from 'components/generic/image-preview/MaterialAndTruckImage'

// utils and helpers
import { b64toBlob, dispatchSnackbar } from 'utils/helpers'
import { generateRequestImagesFunction } from 'utils/images'

const GrossTareImageGrid = ({ title, weight, images, saveImages, hideButton }) => {
  const dispatch = useDispatch()
  const imageRef = useRef()
  const fileInputRef = useRef(null)

  const { defaultYardUm } = useSelector(state => state.settings)

  const [cameraIsOn, setCameraIsOn] = useState(false)
  const { devices, selectedCameraIds, scaleCameras } = useSelector(state => state.devices)
  const useNativeCamera =
    selectedCameraIds?.length === 0 && Object.values(scaleCameras || {}).every(cameras => !cameras?.length)

  const { requestImages } = generateRequestImagesFunction()
  const [uploadOrgImage, { isLoading }] = useUploadOrgImageMutation()
  const [lightboxOpen, setLightboxOpen] = useState(false)

  const renderContent = () => {
    if (cameraIsOn) {
      return (
        <Webcam
          ref={imageRef}
          screenshotFormat="image/jpeg"
          screenshotQuality={1}
          forceScreenshotSourceSize
          height={218}
          width={460}
          videoConstraints={{
            height: 1100,
            width: 1710,
            facingMode: 'environment',
          }}
          onClick={handleClick}
        />
      )
    }
    return images?.length > 0 ? (
      images.map(image => {
        const deviceName = devices?.find(device => device.id === image.deviceId)?.name || 'Camera Unknown'

        return (
          <MaterialAndTruckImage
            imageFilename={image.imageFilename}
            deviceName={deviceName}
            isLoading={isLoading}
            onImageClick={() => setLightboxOpen(true)}
          />
        )
      })
    ) : (
      <MaterialAndTruckImage imageFilename="" deviceName="" isLoading={isLoading} />
    )
  }

  const renderButtonText = () => {
    if (!useNativeCamera) return 'Recapture with same cameras'
    if (cameraIsOn) return 'Capture'
    return images?.length > 0 ? 'Retake Photo' : 'Take Photo'
  }

  const handleClick = async () => {
    if (!useNativeCamera) {
      const deviceIds = images.map(image => image.deviceId).filter(id => id !== 'native' && id !== 'upload')
      const idsToUse = deviceIds.length > 0 ? deviceIds : selectedCameraIds
      requestImages(saveImages, null, idsToUse)
    } else if (cameraIsOn) {
      setCameraIsOn(false)
      const newImage = imageRef.current.getScreenshot()
      if (!newImage) return

      const formData = new FormData()
      formData.append('image', b64toBlob(newImage))

      await uploadOrgImage({ body: formData })
        .unwrap()
        .then(response => saveImages([{ deviceId: 'native', imageFilename: response?.imageName }]))
        .catch(() => dispatchSnackbar(dispatch, 'error', 'An error occurred while uploading your photo'))
    } else {
      setCameraIsOn(true)
    }
  }

  const handleUploadClick = () => {
    fileInputRef.current.click()
  }

  const handleFileChange = async event => {
    const file = event.target.files[0]
    if (!file) {
      return
    }

    const formData = new FormData()
    formData.append('image', file)

    await uploadOrgImage({ body: formData })
      .unwrap()
      .then(response => saveImages([{ deviceId: 'upload', imageFilename: response?.imageName }]))
      .catch(() => dispatchSnackbar(dispatch, 'error', 'An error occurred while uploading your photo'))
  }

  const renderButtons = () => {
    if (hideButton) return null

    if (useNativeCamera) {
      return (
        <>
          <Grid item xs={6}>
            <Button fullWidth variant="outlined" onClick={handleClick}>
              {renderButtonText()}
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button fullWidth variant="outlined" onClick={handleUploadClick}>
              Upload Photo
            </Button>
          </Grid>
        </>
      )
    }

    return (
      <>
        {images?.length > 0 && (
          <Grid item xs={12}>
            <Button fullWidth variant="outlined" onClick={handleClick}>
              Recapture with same cameras
            </Button>
          </Grid>
        )}
        <Grid item xs={12}>
          <Button fullWidth variant="outlined" onClick={() => requestImages(saveImages)}>
            {`${images?.length > 0 ? 'Recapture' : 'Capture'} with selected cameras`}
          </Button>
        </Grid>
      </>
    )
  }

  return (
    <Stack rowGap={1} pb={3}>
      <Typography variant="h6">{title}</Typography>
      <TextField
        name="weight"
        value={weight}
        label="Weight"
        variant="standard"
        InputProps={{
          inputComponent: InputFormat,
          disableUnderline: true,
          inputProps: { suffix: ` ${defaultYardUm}` },
        }}
      />
      <Grid flexWrap="wrap" container>
        {renderContent()}
      </Grid>
      <Grid container spacing={2}>
        {renderButtons()}
      </Grid>
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleFileChange} />
      <GrossTareImagesLightbox
        lightboxOpen={lightboxOpen}
        lightboxImages={images.map(image => image.imageFilename)}
        onClose={() => setLightboxOpen(false)}
      />
    </Stack>
  )
}

GrossTareImageGrid.propTypes = {
  title: PropTypes.string,
  weight: PropTypes.number,
  images: PropTypes.array,
  saveImages: PropTypes.func.isRequired,
  hideButton: PropTypes.bool,
}

GrossTareImageGrid.defaultProps = {
  title: '',
  weight: 0,
  images: [],
  hideButton: false,
}

export default GrossTareImageGrid
